// 路由配置
import { Suspense, FC, useEffect, useState } from 'react';
import { Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';

import { TransitionGroup, CSSTransition } from 'react-transition-group';
import { Spin, message } from 'antd';
import Layout from '@/layout';
import NotFound from '@/common/components/NotFound';
import { isObject, matchQuery } from '@lhb/func';
import { useMethods } from '@lhb/hook';
import { getCookie, setCookie, singleLogout } from '@lhb/cache';

import { routersConfig } from '@/router/router';
import { UPDATE_USER } from '@/store/common';
import { onLogin, onLogout, onNavigate } from '@/common/document-event/on';
import RouterControl from './router/control'; // 路由守卫
import { get } from './common/request';
import { dispatchLogout, dispatchNavigate } from './common/document-event/dispatch';
import Feedback from './common/components/business/Feedback';

const routeList = routersConfig(); // 不能在jsx内执行，需要在Main函数之前就调用，才能获取到正确的页面组件
// 需要添加反馈按钮的页面路由
const feedbackRouters = ['/', '/demand/detail', '/demand/edit', '/demand/quotePrice', '/open/certificate', '/open/detail', '/open', '/open/placeList', '/open/quotePrice'];

const Main: FC<{}> = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isOpen = location.pathname.split('/')[1] === 'open';
  const [dismissLogin, setDismissLogin] = useState(true);
  const [routerTitle, setRouterTitle] = useState('');
  const methods = useMethods({
    listenLogin() {
      const validTime = dismissLogin ? 60 * 60 * 24 * 30 : '';
      onLogin(async (data) => {
        let timeWait = 100;
        if (process.env.NODE_ENV !== 'development') { // 开发环境屏蔽
          if (isObject(data.token)) { // 如果是对象，就上报
            // 主动send事件
            window.LHBbigdata.send({
              event_id: 'kunlun_new_cookie_monitoring_token', // 事件id
              msg: data, // 额外需要插入的业务信息
            });
            timeWait = 200;
          }
        }
        setCookie('kunlun_token', data.token, validTime);
        setCookie('kunlun_user_id', data.employeeId, validTime);
        data.tenantId && setCookie('tenant_id', data.tenantId, validTime);
        message.success('登录成功');
        // 刷新页面
        setTimeout(() => {
          window.location.reload();
        }, timeWait);
      });
    },
    listenLogout() {
      onLogout(async (data) => {
        // 触发子站点的登录事件
        Object.assign(data, { type: 'logout' }); // 类型为登出

        !data.noHint && message.success('退出成功~');
        const token = getCookie('kunlun_token');
        singleLogout();
        if (token) {
          await get('/logout');
        } else {
          dispatchNavigate('/');
        }
        setTimeout(() => {
          if (process.env.NODE_ENV === 'development') { // 开发的话直接使用自带的一套登录
            if (token) {
              window.location.reload();
            } else {
              dispatchNavigate('/');
            }
          } else { // 跳转到kunlun去做登录
            window.location.href = `${process.env.KUNLUN_URL}?origin=${encodeURIComponent(window.location.href)}`;
          }
        }, 100);
      });
    },
    getUserInfo() {
      // 对外开放的页面不需要获取用户信息,只有非对外的才需要
      if (!isOpen) {
        const token = getCookie('kunlun_token');
        if (token) {
          get('/currentUser', {}, true).then((data) => {
            dispatch(UPDATE_USER(data));
          }, () => {
            dispatchLogout();
          });
        }
      }
    },
    curRouteTitle () {
      if (feedbackRouters.includes(location.pathname)) {
        const temp = routeList.filter(item => item.path === location.pathname);
        setRouterTitle(Array.isArray(temp) && temp.length ? (temp[0]?.meta?.title || '') : '');
      }
    }
  });
  useEffect(() => {
    methods.listenLogin();

  }, [dismissLogin]);
  useEffect(() => {
    methods.listenLogout();
    methods.getUserInfo();
    onNavigate((path :any) => {
      console.log(path, '77677');
      navigate(path);
      window.scrollTo(0, 0);
    });
    // methods.verifyAuth();

  }, []);

  useEffect(() => {
    methods.curRouteTitle();
  }, [location]);

  return (
    <RouterControl locationTo={location} isOpen={isOpen} routeList={routeList} setDismissLogin={setDismissLogin}>
      <Layout routeList={routeList} isOpen={isOpen || !!matchQuery(location.search, 'source')}>
        <Suspense fallback={<Spin />}>
          <TransitionGroup component={null}>
            <CSSTransition key={location.pathname} classNames='fade' timeout={300}>
              {/* https://juejin.cn/post/6844903922574819342#heading-7 */}
              <Routes location={location}>
                {routeList.map((item) => (
                  <Route key={item.path} path={item.path} element={<item.component location={location} />}></Route>
                ))}
                {/* 匹配找不到的路由 */}
                <Route path='*' element={<NotFound />}></Route>
              </Routes>
            </CSSTransition>
          </TransitionGroup>
        </Suspense>
        {/* 反馈工具 */}
        {feedbackRouters.includes(location.pathname) && <Feedback title={routerTitle}/>}
      </Layout>
    </RouterControl>
  );
};

export default Main;
